import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, AlertTriangle, Sparkles, TrendingUp, Clock, CheckCircle2, BarChart3, RefreshCw, Search, Trash2, Activity, Undo2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { fiveStarApi } from '../../../lib/api';

const STATUS_LABEL = {
  manager_rating: 'Awaiting Manager Rating',
  area_manager_review: 'Awaiting Area Manager',
  manager_synthesis: 'Awaiting Synthesis',
  signed_off: 'Signed Off — Discussion Pending',
  discussion_pending: 'Discussion Pending',
  discussed: 'Complete',
  expired: 'Expired (auto-closed)',
};

const STATUS_COLOR = {
  manager_rating: 'bg-amber-50 text-amber-700 border-amber-200',
  area_manager_review: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  manager_synthesis: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  signed_off: 'bg-violet-50 text-violet-700 border-violet-200',
  discussion_pending: 'bg-blue-50 text-blue-700 border-blue-200',
  discussed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  expired: 'bg-rose-50 text-rose-700 border-rose-300',
};

const Kpi = ({ icon: Icon, label, value, sub, tone = 'slate', testId }) => (
  <div
    data-testid={testId}
    className={`rounded-xl border bg-white p-4 border-${tone}-200`}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`text-2xl font-extrabold mt-1 text-${tone}-700`}>{value ?? '—'}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
      <Icon className={`w-5 h-5 text-${tone}-400`} />
    </div>
  </div>
);

const FunnelBar = ({ label, value, max, color }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex justify-between text-[11px] text-slate-600 mb-1">
        <span className="font-semibold">{label}</span>
        <span className="font-bold">{value}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%`, transition: 'width 300ms ease' }} />
      </div>
    </div>
  );
};

export const FiveStarAnalyticsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Admin override modal (super-admin — change manager / AM / rewind status)
  const [overrideReview, setOverrideReview] = useState(null);
  const [overrideCandidates, setOverrideCandidates] = useState([]);
  const [overrideForm, setOverrideForm] = useState({ manager_user_id: '', area_manager_user_id: '', status: '', reason: '' });
  const [overrideSaving, setOverrideSaving] = useState(false);

  const load = async (showSpinner = true) => {
    if (showSpinner) setLoading(true); else setReloading(true);
    try {
      const d = await fiveStarApi.adminAnalytics();
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Failed to load 5-Star analytics');
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredReviews = useMemo(() => {
    if (!data?.reviews) return [];
    const needle = q.trim().toLowerCase();
    return data.reviews.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!needle) return true;
      return (
        (r.employee_name || '').toLowerCase().includes(needle) ||
        (r.employee_email || '').toLowerCase().includes(needle) ||
        (r.manager_name || '').toLowerCase().includes(needle) ||
        (r.cycle_name || '').toLowerCase().includes(needle)
      );
    });
  }, [data, q, statusFilter]);

  const handleDelete = async (review) => {
    if (!window.confirm(`PERMANENTLY delete the ${review.cycle_name} review for ${review.employee_name}?\nThis action is logged. Cannot be undone.`)) return;
    try {
      await fiveStarApi.deleteReview(review.id);
      toast.success('Review deleted — action logged');
      // Optimistically remove from local state
      setData(prev => prev ? {
        ...prev,
        reviews: prev.reviews.filter(r => r.id !== review.id),
        kpis: { ...prev.kpis, total_reviews: Math.max(0, (prev.kpis.total_reviews || 0) - 1) },
      } : prev);
      // Reload after a beat so the activity feed updates too
      setTimeout(() => load(false), 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Delete failed');
    }
  };

  const handleRestore = async (reviewId, label) => {
    if (!window.confirm(`Restore the review for ${label || reviewId}?\nThis will re-insert the review with all its original data.`)) return;
    try {
      await fiveStarApi.restoreReview(reviewId);
      toast.success('Review restored');
      setTimeout(() => load(false), 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Restore failed');
    }
  };

  // ── Admin Override (super-admin only) ─────────────────────────────────
  const OVERRIDE_STATUS_ORDER = ['manager_rating', 'area_manager_review', 'manager_synthesis', 'signed_off', 'discussion_pending', 'discussed', 'expired'];
  const OVERRIDE_STATUS_LABEL = {
    manager_rating: '1 · Manager Rating',
    area_manager_review: '2 · Area Manager Review',
    manager_synthesis: '3 · Manager Synthesis',
    signed_off: '4 · Signed Off (awaiting discussion)',
    discussion_pending: '5 · Discussion Pending',
    discussed: '6 · Discussed (complete)',
    expired: '7 · Expired (auto-closed)',
  };

  const openOverride = async (review) => {
    setOverrideReview(review);
    setOverrideForm({
      manager_user_id: review.manager_user_id || '',
      area_manager_user_id: review.area_manager_user_id || '',
      status: review.status || '',
      reason: '',
    });
    try {
      const d = await fiveStarApi.listEligibleManagers();
      setOverrideCandidates((d?.managers || []).filter(m => m.id !== review.employee_user_id));
    } catch {
      setOverrideCandidates([]);
    }
  };

  const submitOverride = async () => {
    const reason = (overrideForm.reason || '').trim();
    if (reason.length < 5) { toast.error('Please provide a reason (min 5 characters)'); return; }
    const payload = { reason };
    if (overrideForm.manager_user_id && overrideForm.manager_user_id !== overrideReview.manager_user_id) payload.manager_user_id = overrideForm.manager_user_id;
    if (overrideForm.area_manager_user_id !== (overrideReview.area_manager_user_id || '')) payload.area_manager_user_id = overrideForm.area_manager_user_id || null;
    if (overrideForm.status && overrideForm.status !== overrideReview.status) payload.status = overrideForm.status;
    if (Object.keys(payload).length === 1) { toast.error('Change at least one field'); return; }
    setOverrideSaving(true);
    try {
      const updated = await fiveStarApi.adminOverride(overrideReview.id, payload);
      toast.success('Review overridden — all captured data preserved');
      // Merge the updated review back into local state so the table refreshes without a full reload
      setData(prev => prev ? {
        ...prev,
        reviews: prev.reviews.map(r => r.id === updated.id ? { ...r, ...updated } : r),
      } : prev);
      setOverrideReview(null);
      setTimeout(() => load(false), 400);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Override failed');
    } finally {
      setOverrideSaving(false);
    }
  };
  const currentStatusIdx = overrideReview ? OVERRIDE_STATUS_ORDER.indexOf(overrideReview.status) : -1;

  if (loading) return <div className="text-sm text-slate-500 py-6">Loading 5-Star analytics…</div>;
  if (!data) return null;

  const { kpis, status_funnel = {}, score_distribution = {}, stuck_reviews = [], ai_usage = {} } = data;
  const maxStatusCount = Math.max(1, ...Object.values(status_funnel));
  const maxScoreBand = Math.max(1, ...Object.values(score_distribution));

  return (
    <div className="space-y-5 mt-6 pt-6 border-t border-slate-200" data-testid="five-star-analytics">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
            <Star className="w-4 h-4 text-amber-500 fill-amber-400" />
            5-Star Feedback &amp; Development
          </div>
          <div className="text-xs text-slate-500">Program-wide review activity, scores, and bottlenecks.</div>
        </div>
        <button
          type="button"
          onClick={() => load(false)}
          disabled={reloading}
          data-testid="five-star-analytics-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${reloading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="five-star-kpis">
        <Kpi icon={BarChart3}    label="Total Reviews"             value={kpis.total_reviews}             tone="slate"    testId="kpi-total" />
        <Kpi icon={CheckCircle2} label="Completed"                 value={kpis.completed}
             sub={`${kpis.completed_this_quarter || 0} this quarter`}                                     tone="emerald"  testId="kpi-completed" />
        <Kpi icon={Clock}        label="In Progress"               value={kpis.in_progress}               tone="amber"    testId="kpi-inprogress" />
        <Kpi icon={Star}         label="Average Final Score"
             value={kpis.avg_final_score != null ? kpis.avg_final_score.toFixed(2) : '—'}
             sub={kpis.avg_days_to_complete != null ? `${kpis.avg_days_to_complete}d avg to complete` : null}
             tone="fuchsia" testId="kpi-avg-score" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi icon={TrendingUp} label="AM Opt-out Rate"           value={`${kpis.am_opt_out_pct ?? 0}%`}    tone="rose"    testId="kpi-am-optout" />
        <Kpi icon={Sparkles}   label="AI Summary Usage"
             value={ai_usage.reviews_using_ai || 0}
             sub={`${ai_usage.total_generations || 0} generations · avg ${ai_usage.avg_generations_per_review || 0}/review`}
             tone="cyan" testId="kpi-ai-usage" />
        <Kpi icon={AlertTriangle} label="Stuck Reviews (>14d)"   value={stuck_reviews.length}              tone="red"     testId="kpi-stuck" />
      </div>

      {/* Funnel + Distribution side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="status-funnel">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Status Funnel</div>
          {Object.keys(status_funnel).length === 0
            ? <div className="text-xs text-slate-400 italic">No reviews yet.</div>
            : Object.entries(status_funnel).map(([s, n]) => (
                <FunnelBar key={s} label={STATUS_LABEL[s] || s} value={n} max={maxStatusCount} color={
                  s === 'discussed' ? 'bg-emerald-500' :
                  s === 'manager_synthesis' ? 'bg-cyan-500' :
                  s === 'area_manager_review' ? 'bg-fuchsia-500' :
                  s === 'manager_rating' ? 'bg-amber-500' :
                  'bg-violet-500'
                } />
              ))}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="score-distribution">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Final Score Distribution</div>
          {Object.values(score_distribution).every(v => v === 0)
            ? <div className="text-xs text-slate-400 italic">No completed reviews yet.</div>
            : Object.entries(score_distribution).map(([band, n]) => (
                <FunnelBar key={band} label={band === '<2' ? 'Concern (<2)' : band === '2-3' ? 'Needs Improvement (2–3)' : band === '3-4' ? 'Solid (3–4)' : 'Strong (4–5)'} value={n} max={maxScoreBand} color={
                  band === '<2' ? 'bg-rose-500' :
                  band === '2-3' ? 'bg-amber-500' :
                  band === '3-4' ? 'bg-cyan-500' :
                  'bg-emerald-500'
                } />
              ))}
        </div>
      </div>

      {/* Stuck reviews */}
      {stuck_reviews.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-5" data-testid="stuck-reviews">
          <div className="text-xs font-bold uppercase tracking-wider text-rose-700 mb-3 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Stuck Reviews — no progress in 14+ days
          </div>
          <div className="space-y-1.5">
            {stuck_reviews.slice(0, 8).map(r => (
              <Link key={r.id} to={`/five-star/review/${r.id}`} className="flex justify-between items-center text-xs px-3 py-2 rounded bg-white border border-rose-100 hover:border-rose-300 transition">
                <span className="font-semibold text-slate-800">{r.employee_name}</span>
                <span className="text-slate-500">{r.cycle_name} · {STATUS_LABEL[r.status] || r.status}</span>
                <span className="font-bold text-rose-600">{r.days_since_update}d</span>
              </Link>
            ))}
            {stuck_reviews.length > 8 && (
              <div className="text-[11px] text-slate-500 italic pl-1 pt-1">…and {stuck_reviews.length - 8} more.</div>
            )}
          </div>
        </div>
      )}

      {/* All reviews table */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="all-reviews-table">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 gap-3 flex-wrap">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
            All Reviews <span className="text-slate-400 normal-case font-normal">({filteredReviews.length})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search employee, manager, cycle…"
                data-testid="reviews-search"
                className="text-xs pl-7 pr-3 py-1.5 rounded-md border border-slate-200 w-64 focus:outline-none focus:border-fuchsia-400"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              data-testid="reviews-status-filter"
              className="text-xs px-2 py-1.5 rounded-md border border-slate-200 focus:outline-none focus:border-fuchsia-400"
            >
              <option value="all">All statuses</option>
              {Object.keys(status_funnel).map(s => (
                <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="reviews-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-100">
                <th className="text-left px-4 py-2 font-bold">Employee</th>
                <th className="text-left px-3 py-2 font-bold">Cycle</th>
                <th className="text-left px-3 py-2 font-bold">Manager</th>
                <th className="text-left px-3 py-2 font-bold">Area Mgr</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-right px-3 py-2 font-bold">Score</th>
                <th className="text-left px-3 py-2 font-bold">Updated</th>
                <th className="text-right px-3 py-2 font-bold w-12">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReviews.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400 italic">No reviews match the current filter.</td></tr>
              )}
              {filteredReviews.map(r => (
                <tr key={r.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition">
                  <td className="px-4 py-2.5">
                    <Link to={`/five-star/review/${r.id}`} className="font-semibold text-slate-900 hover:text-fuchsia-600" data-testid={`analytics-row-${r.id}`}>
                      {r.employee_name}
                    </Link>
                    <div className="text-[10px] text-slate-400">{r.employee_email}</div>
                  </td>
                  <td className="px-3 py-2.5 text-slate-700">
                    {r.cycle_name}
                    {r.review_type && <span className="text-[10px] text-slate-400 ml-1">· {r.review_type}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-700">{r.manager_name || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-700">
                    {r.area_manager_name || <span className="text-slate-400 italic">none</span>}
                    {r.area_manager_opted_out && <span className="ml-1 text-[10px] text-rose-500">(opted out)</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${STATUS_COLOR[r.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold text-slate-900">
                    {r.final_overall_score != null
                      ? <span className="inline-flex items-center gap-1 text-fuchsia-600"><Star className="w-3 h-3 fill-fuchsia-400 text-fuchsia-400" /> {r.final_overall_score.toFixed(1)}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 text-[11px] whitespace-nowrap">
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => openOverride(r)}
                        data-testid={`analytics-override-${r.id}`}
                        title="Admin Override — change manager / AM / rewind status"
                        className="p-1.5 rounded-md border border-amber-300 text-amber-600 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition"
                      >
                        <Shield className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(r)}
                        data-testid={`analytics-delete-${r.id}`}
                        title="Permanently delete this review (logged in admin activity)"
                        className="p-1.5 rounded-md border border-rose-200 text-rose-500 hover:bg-rose-600 hover:text-white hover:border-rose-600 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent admin activity related to 5-Star reviews */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="five-star-activity-log">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-slate-400" />
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
            5-Star Activity Log <span className="text-slate-400 normal-case font-normal">({data.recent_activity?.length || 0} most recent)</span>
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {(!data.recent_activity || data.recent_activity.length === 0) ? (
            <div className="px-5 py-6 text-xs text-slate-400 italic text-center">No 5-Star activity yet.</div>
          ) : data.recent_activity.map(ev => {
            const isDelete = ev.event_type === 'five_star_review_deleted';
            const isInitiate = ev.event_type === 'five_star_initiated';
            const isOptOut = ev.event_type === 'five_star_am_opt_out';
            const isRestore = ev.event_type === 'five_star_review_restored';
            return (
              <div key={ev.id} className="px-5 py-2.5 border-b border-slate-50 last:border-b-0 flex items-start gap-3 hover:bg-slate-50/60 transition">
                <span className={`mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${
                  isDelete ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                  isInitiate ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                  isOptOut ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                  isRestore ? 'bg-cyan-50 text-cyan-700 border border-cyan-200' :
                  'bg-slate-100 text-slate-700 border border-slate-200'
                }`}>
                  {isDelete ? 'Deleted' : isInitiate ? 'Initiated' : isOptOut ? 'AM Opt-out' : isRestore ? 'Restored' : ev.event_type}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-700 break-words">{ev.details || '—'}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    {ev.user_email || 'system'}
                    {ev.timestamp && <> · {new Date(ev.timestamp).toLocaleString()}</>}
                  </div>
                </div>
                {isDelete && ev.restorable && ev.review_id && (
                  <button
                    type="button"
                    onClick={() => handleRestore(ev.review_id, (ev.target_user_email || ev.review_id))}
                    data-testid={`restore-review-${ev.review_id}`}
                    title="Restore this review from snapshot (available 30 days after delete)"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border border-cyan-200 text-cyan-700 hover:bg-cyan-600 hover:text-white hover:border-cyan-600 transition whitespace-nowrap"
                  >
                    <Undo2 className="w-3 h-3" /> Restore
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {overrideReview && (
        <div className="fixed inset-0 bg-slate-900/55 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !overrideSaving && setOverrideReview(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="analytics-override-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" /> Admin Override
                </h3>
                <div className="text-xs text-slate-500 mt-1">
                  {overrideReview.employee_name} · {overrideReview.cycle_name}
                </div>
              </div>
              <button onClick={() => !overrideSaving && setOverrideReview(null)} className="p-1 rounded hover:bg-slate-100" type="button">
                <span className="text-slate-400 text-lg">×</span>
              </button>
            </div>

            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Super-admin surgical tool.</strong> Change any subset of manager, area manager, and status.
                Status can only be <strong>rewound</strong>. All captured data is <strong>preserved</strong> — users simply resubmit to overwrite.
              </div>
            </div>

            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Manager (DOR)</label>
            <select
              value={overrideForm.manager_user_id}
              onChange={(e) => setOverrideForm({ ...overrideForm, manager_user_id: e.target.value })}
              data-testid="analytics-override-manager-select"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg mb-1 focus:border-amber-400 focus:outline-none"
            >
              <option value="">— Keep current ({overrideReview.manager_name}) —</option>
              {overrideCandidates.map(c => <option key={c.id} value={c.id}>{c.display_name} ({c.email})</option>)}
            </select>
            <div className="text-[11px] text-slate-400 mb-4">Current: <strong>{overrideReview.manager_name}</strong></div>

            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Area Manager</label>
            <select
              value={overrideForm.area_manager_user_id}
              onChange={(e) => setOverrideForm({ ...overrideForm, area_manager_user_id: e.target.value })}
              data-testid="analytics-override-am-select"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg mb-1 focus:border-amber-400 focus:outline-none"
            >
              <option value="">— Keep current —</option>
              {overrideCandidates.map(c => <option key={c.id} value={c.id}>{c.display_name} ({c.email})</option>)}
            </select>
            <div className="text-[11px] text-slate-400 mb-4">
              Current: {overrideReview.area_manager_name ? <strong>{overrideReview.area_manager_name}</strong> : <em>none</em>}
              <span className="block mt-0.5">💡 Picking the same person as the Manager marks the review as manager-self-attested and skips the Area Manager step.</span>
            </div>

            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Status (rewind only)</label>
            <select
              value={overrideForm.status}
              onChange={(e) => setOverrideForm({ ...overrideForm, status: e.target.value })}
              data-testid="analytics-override-status-select"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg mb-1 focus:border-amber-400 focus:outline-none"
            >
              {OVERRIDE_STATUS_ORDER.map((s, i) => (
                <option key={s} value={s} disabled={currentStatusIdx >= 0 && i > currentStatusIdx && s !== 'expired'}>
                  {OVERRIDE_STATUS_LABEL[s]}{currentStatusIdx >= 0 && i > currentStatusIdx && s !== 'expired' ? ' (forward — disabled)' : ''}
                  {s === overrideReview.status ? ' · current' : ''}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-slate-400 mb-4">Rewinding preserves all captured data.</div>

            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Reason <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={overrideForm.reason}
              onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
              placeholder="Why is this override necessary? (min 5 chars — visible in the Activity Log)"
              data-testid="analytics-override-reason"
              rows={3}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:border-amber-400 focus:outline-none resize-y"
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setOverrideReview(null)} disabled={overrideSaving}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={submitOverride}
                disabled={overrideSaving || (overrideForm.reason || '').trim().length < 5}
                data-testid="analytics-override-submit"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-amber-600 text-white shadow hover:bg-amber-700 disabled:opacity-50"
              >
                <Shield className="w-4 h-4" /> {overrideSaving ? 'Saving…' : 'Apply Override'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiveStarAnalyticsSection;
